import React, { useCallback, useEffect, useMemo, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import axios from "axios";
import CommunityMeetups from "../components/home/CommunityMeetups";
import "../styles/calendar.css";
import { buildRichHtmlMarkup, getPlainTextFromHtml } from "../utils/richHtml";

const emptyCalendarForm = {
  kind: "event",
  id: "",
  title: "",
  eventCategory: "Udyog Connect Event",
  eventDate: "",
  startTime: "09:00",
  endTime: "10:00",
  venue: "",
  status: "open",
  publicationStatus: "draft",
  whoCanApply: "anyone",
  link: "",
  capacity: "",
  nativeRegistrationEnabled: true,
  showPrimaryInterest: true,
  paymentEnabled: false,
  paymentAmountUsd: "",
  paymentLabel: "General Admission",
  city: "",
  state: "",
  venueName: "",
  addressLine: "",
  zipCode: "",
  timezone: "America/New_York",
  topic: "",
  description: "",
  walkInAllowed: true,
  onlineAvailable: true,
  meetupCapacity: 35,
  meetupStatus: "scheduled",
  meetupPublicationStatus: "draft",
};

const toDateTime = (date, time) => {
  if (!date) return null;
  const datePart = String(date).slice(0, 10);
  return `${datePart}T${time || "00:00:00"}`;
};

const toInputDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }
  return "";
};

const normalizeTime = (value, fallback = "") => {
  if (!value) return fallback;
  return String(value).slice(0, 5);
};

const normalizeText = (value) => getPlainTextFromHtml(value).replace(/\s+/g, " ").trim();

const buildCalendarEventTitle = (description) => {
  const cleaned = normalizeText(description);
  if (!cleaned) return "Udyog Connect Event";
  const firstSentence = cleaned.split(/[.!?]/)[0] || cleaned;
  const title = firstSentence.length >= 24 ? firstSentence : cleaned;
  return title.length > 86 ? `${title.slice(0, 83).trim()}…` : title;
};

const buildEventRegistrationUrl = (eventId) =>
  `${window.location.origin}/2.0/event/${encodeURIComponent(eventId)}/register`;

const buildMeetupShareUrl = (meetupKey) =>
  `${window.location.origin}/2.0/community-meetup/${encodeURIComponent(meetupKey)}/register`;

const buildEventImageUrl = (imageId) =>
  `${process.env.REACT_APP_API_BASE_URL}/api/admin/events/images/${imageId}`;

const copyText = async (value) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  window.prompt("Copy this share link:", value);
};

const buildSocialUrl = (shareUrl, text, channel) => {
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(text || "Udyog Connect event");
  const urls = {
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    x: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
  };
  return urls[channel];
};

const CalendarPage = () => {
  const token = localStorage.getItem("token");
  const isAdmin = String(localStorage.getItem("accessLevel") || "").toLowerCase() === "admin";
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [udyogEvents, setUdyogEvents] = useState([]);
  const [trainingPrograms, setTrainingPrograms] = useState([]);
  const [communityMeetups, setCommunityMeetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [partnerFilter, setPartnerFilter] = useState("all");
  const [eventFormOpen, setEventFormOpen] = useState(false);
  const [eventFormMode, setEventFormMode] = useState("create");
  const [eventForm, setEventForm] = useState(emptyCalendarForm);
  const [savingEvent, setSavingEvent] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [lastCreatedShareUrl, setLastCreatedShareUrl] = useState("");
  const [meetupRefreshKey, setMeetupRefreshKey] = useState(0);
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    const [eventsRes, trainingRes, meetupsRes] = await Promise.allSettled([
      axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/events${isAdmin ? "?includeDrafts=true" : ""}`,
        isAdmin ? { headers: authHeaders } : undefined
      ),
      axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/2.0/training-programs`),
      // Request the complete meetup archive here. The featured cards above
      // continue to use the default upcoming-only endpoint.
      axios.get(
        isAdmin
          ? `${process.env.REACT_APP_API_BASE_URL}/api/admin/community-meetups?includeInactive=true`
          : `${process.env.REACT_APP_API_BASE_URL}/api/community-meetups?includePast=true`,
        isAdmin ? { headers: authHeaders } : undefined
      ),
    ]);

    if (eventsRes.status === "fulfilled") {
      const rows = Array.isArray(eventsRes.value.data) ? eventsRes.value.data : [];
      const enrichedEvents = await Promise.all(
        rows.map(async (event) => {
          try {
            const imageRes = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/api/admin/events/${event.id}/images`
            );
            const images = (Array.isArray(imageRes.data) ? imageRes.data : []).map((image) => ({
              id: image.id,
              url: buildEventImageUrl(image.id),
            }));
            return { ...event, images };
          } catch (error) {
            console.warn("Unable to load calendar event images", event.id, error);
            return { ...event, images: [] };
          }
        })
      );
      setUdyogEvents(enrichedEvents);
    }

    if (trainingRes.status === "fulfilled") {
      setTrainingPrograms(trainingRes.value.data?.data || []);
    }

    if (meetupsRes.status === "fulfilled") {
      setCommunityMeetups(
        Array.isArray(meetupsRes.value.data?.meetups) ? meetupsRes.value.data.meetups : []
      );
    }

    setLoading(false);
  }, [authHeaders, isAdmin]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  const calendarEvents = useMemo(() => {
    const mappedEvents = udyogEvents
      .map((event) => ({
        id: `event-${event.id}`,
        title: `${event.publicationStatus === "draft" ? "Draft — " : ""}${event.eventTitle || buildCalendarEventTitle(event.description)}`,
        start: toDateTime(event.eventDate, event.startTime),
        end: event.endTime ? toDateTime(event.eventDate, event.endTime) : null,
        backgroundColor: event.publicationStatus === "draft" ? "#64748b" : "#1746d1",
        borderColor: event.publicationStatus === "draft" ? "#475569" : "#1746d1",
        extendedProps: {
          type: event.eventCategory || "Udyog Connect Event",
          sourceType: "event",
          rawId: event.id,
          rawEvent: event,
          location: event.venue,
          audience: event.whoCanApply,
          partner: "Udyog Connect",
          description: event.description,
          applyLink: event.publicationStatus === "draft"
            ? ""
            : (event.nativeRegistrationEnabled === false
              ? event.link
              : (event.registrationUrl || buildEventRegistrationUrl(event.id))),
          externalRegistrationUrl: event.externalRegistrationUrl || event.link,
          registrationUrl: event.publicationStatus === "draft"
            ? ""
            : (event.registrationUrl || buildEventRegistrationUrl(event.id)),
          status: event.status,
          publicationStatus: event.publicationStatus || "published",
          images: event.images || [],
          capacity: event.capacity,
          seatsLeft: event.seatsLeft,
          paymentSettings: event.paymentSettings || {},
        },
      }))
      .filter((event) => event.start);

    const mappedTraining = trainingPrograms.map((program) => ({
      id: `training-${program.id}`,
      title: program.title,
      start: program.start,
      end: program.end,
      backgroundColor: "#0f766e",
      borderColor: "#0f766e",
      extendedProps: {
        type: "Training Program",
        sourceType: "training",
        rawId: program.id,
        location: program.location,
        audience: program.audience,
        partner: program.partner,
        description: program.description,
        applyLink: program.applyLink,
        status: program.status,
      },
    }));

    const mappedMeetups = communityMeetups
      .map((meetup) => ({
        id: `meetup-${meetup.id}`,
        title: `${meetup.publicationStatus === "draft" ? "Draft — " : ""}${meetup.title || "UC Community Meetup"}`,
        start: toDateTime(meetup.eventDate, meetup.startTime),
        end: meetup.endTime ? toDateTime(meetup.eventDate, meetup.endTime) : null,
        backgroundColor: meetup.publicationStatus === "draft" ? "#64748b" : "#f59e0b",
        borderColor: meetup.publicationStatus === "draft" ? "#475569" : "#f59e0b",
        textColor: "#111827",
        extendedProps: {
          type: "UC Community Meetup",
          sourceType: "meetup",
          rawId: meetup.id,
          rawMeetup: meetup,
          location: [meetup.venueName, meetup.city, meetup.state].filter(Boolean).join(" | "),
          audience: meetup.walkInAllowed ? "Walk-ins welcome" : "Registration required",
          partner: "UC Community",
          description: meetup.topic || meetup.description,
          applyLink: meetup.publicationStatus === "draft"
            ? ""
            : buildMeetupShareUrl(meetup.slug || meetup.id),
          status: meetup.status,
          publicationStatus: meetup.publicationStatus || "published",
          slug: meetup.slug,
        },
      }))
      .filter((event) => event.start);

    const allEvents = [...mappedEvents, ...mappedTraining, ...mappedMeetups];
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return allEvents.filter((event) => {
      const props = event.extendedProps;
      const matchesType = typeFilter === "all" || props.type === typeFilter;
      const matchesPartner = partnerFilter === "all" || props.partner === partnerFilter;
      const searchable = [
        event.title,
        props.type,
        props.location,
        props.audience,
        props.partner,
        props.description,
        props.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return matchesType && matchesPartner && searchable.includes(normalizedSearch);
    });
  }, [udyogEvents, trainingPrograms, communityMeetups, searchTerm, typeFilter, partnerFilter]);

  const partners = useMemo(
    () =>
      Array.from(
        new Set(calendarEvents.map((event) => event.extendedProps.partner).filter(Boolean))
      ).sort(),
    [calendarEvents]
  );

  const calendarArchiveEvents = useMemo(() => {
    // Keep the newest scheduled date at the top, then retain every older item
    // in the scrollable list so calendar history remains discoverable.
    return [...calendarEvents].sort((a, b) => new Date(b.start) - new Date(a.start));
  }, [calendarEvents]);

  const formatDateTime = (date) => {
    if (!date) return "";
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  };

  const openEventDetails = useCallback((event) => {
    const rawId = event.extendedProps.rawId;
    const sourceType = event.extendedProps.sourceType;
    const shareUrl = sourceType === "event"
      ? (event.extendedProps.registrationUrl || buildEventRegistrationUrl(rawId))
      : sourceType === "meetup"
        ? buildMeetupShareUrl(event.extendedProps.slug || rawId)
        : "";

    setSelectedEvent({
      id: event.id,
      rawId,
      rawEvent: event.extendedProps.rawEvent,
      rawMeetup: event.extendedProps.rawMeetup,
      sourceType,
      title: event.title,
      start: event.start ? new Date(event.start) : null,
      end: event.end ? new Date(event.end) : null,
      type: event.extendedProps.type,
      location: event.extendedProps.location,
      audience: event.extendedProps.audience,
      partner: event.extendedProps.partner,
      description: event.extendedProps.description,
      applyLink: event.extendedProps.applyLink,
      externalRegistrationUrl: event.extendedProps.externalRegistrationUrl,
      registrationUrl: event.extendedProps.registrationUrl,
      status: event.extendedProps.status,
      shareUrl,
      images: event.extendedProps.images || [],
      capacity: event.extendedProps.capacity,
      seatsLeft: event.extendedProps.seatsLeft,
      paymentSettings: event.extendedProps.paymentSettings || {},
    });
  }, []);

  useEffect(() => {
    if (deepLinkHandled || calendarEvents.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const eventKey = params.get("event");
    const meetupKey = params.get("meetup");
    if (!eventKey && !meetupKey) return;

    const match = calendarEvents.find(
      (event) =>
        (eventKey && (
          event.id === eventKey ||
          String(event.extendedProps.rawId) === eventKey ||
          `event-${event.extendedProps.rawId}` === eventKey
        )) ||
        (meetupKey && event.extendedProps.sourceType === "meetup" && (
          event.id === meetupKey ||
          event.extendedProps.slug === meetupKey ||
          String(event.extendedProps.rawId) === meetupKey ||
          `meetup-${event.extendedProps.rawId}` === meetupKey
        ))
    );

    if (match) {
      openEventDetails(match);
      setDeepLinkHandled(true);
    }
  }, [calendarEvents, deepLinkHandled, openEventDetails]);

  const handleEventClick = (clickInfo) => {
    openEventDetails(clickInfo.event);
  };

  const openCreateCalendarItem = (dateStr = toInputDate(new Date()), kind = "event") => {
    if (!isAdmin) return;
    setEventForm({
      ...emptyCalendarForm,
      kind,
      eventDate: dateStr,
      title: kind === "event" ? "UC public orientation" : "",
      eventCategory: "Udyog Connect Event",
      city: "Atlanta Metro",
      state: "GA",
      venueName: "Local library / community room",
      addressLine: "Venue confirmation will be shared after registration",
      topic: "Second-income ideas, student internships, and community routing",
      description: kind === "event"
        ? "<h2>About this event</h2><p>A friendly UC public session for opportunity seekers and community partners.</p>"
        : "A friendly UC public session for opportunity seekers and community partners.",
    });
    setEventFormMode("create");
    setFormMessage("");
    setLastCreatedShareUrl("");
    setEventFormOpen(true);
  };

  const openEditCalendarEvent = (eventDetails) => {
    if (!isAdmin || eventDetails.sourceType !== "event" || !eventDetails.rawEvent) return;
    const raw = eventDetails.rawEvent;
    setEventForm({
      ...emptyCalendarForm,
      kind: "event",
      id: raw.id,
      title: raw.eventTitle || eventDetails.title || "",
      eventCategory: raw.eventCategory || "Udyog Connect Event",
      description: raw.description || "",
      eventDate: toInputDate(raw.eventDate),
      startTime: normalizeTime(raw.startTime, "09:00"),
      endTime: normalizeTime(raw.endTime, "10:00"),
      venue: raw.venue || "",
      status: raw.status || "open",
      publicationStatus: raw.publicationStatus || "published",
      whoCanApply: raw.whoCanApply || "anyone",
      link: raw.externalRegistrationUrl || raw.link || "",
      capacity: raw.capacity || "",
      nativeRegistrationEnabled: raw.nativeRegistrationEnabled !== false,
      showPrimaryInterest: raw.showPrimaryInterest !== false,
      paymentEnabled: Boolean(raw.paymentSettings?.enabled),
      paymentAmountUsd: raw.paymentSettings?.amountUsd || "",
      paymentLabel: raw.paymentSettings?.label || "General Admission",
    });
    setEventFormMode("edit");
    setFormMessage("");
    setLastCreatedShareUrl("");
    setEventFormOpen(true);
    setSelectedEvent(null);
  };

  const updateEventForm = (key, value) => {
    setEventForm((current) => ({ ...current, [key]: value }));
  };

  const submitCalendarForm = async (event) => {
    event.preventDefault();
    setSavingEvent(true);
    setFormMessage("");
    setLastCreatedShareUrl("");

    try {
      if (eventForm.kind === "event") {
        if (!eventForm.eventDate || !eventForm.startTime || !eventForm.endTime || !eventForm.title.trim() || !getPlainTextFromHtml(eventForm.description).trim() || !eventForm.venue.trim()) {
          setFormMessage("Please enter date, time, title, rich event content, and venue.");
          setSavingEvent(false);
          return;
        }

        const payload = {
          eventTitle: eventForm.title,
          eventCategory: eventForm.eventCategory,
          eventDate: eventForm.eventDate,
          description: eventForm.description,
          venue: eventForm.venue,
          startTime: eventForm.startTime,
          endTime: eventForm.endTime,
          status: eventForm.status,
          publicationStatus: eventForm.publicationStatus,
          whoCanApply: eventForm.whoCanApply,
          link: eventForm.link,
          capacity: eventForm.capacity === "" ? null : Number(eventForm.capacity),
          nativeRegistrationEnabled: Boolean(eventForm.nativeRegistrationEnabled),
          showPrimaryInterest: Boolean(eventForm.showPrimaryInterest),
          paymentSettings: {
            enabled: Boolean(eventForm.paymentEnabled),
            amountUsd: eventForm.paymentEnabled ? Number(eventForm.paymentAmountUsd || 0) : 0,
            label: eventForm.paymentLabel || "General Admission",
            addToCartEnabled: true,
          },
        };

        const response = eventFormMode === "edit" && eventForm.id
          ? await axios.put(
              `${process.env.REACT_APP_API_BASE_URL}/api/events/${eventForm.id}`,
              payload,
              { headers: authHeaders }
            )
          : await axios.post(
              `${process.env.REACT_APP_API_BASE_URL}/api/events`,
              payload,
              { headers: authHeaders }
            );

        const savedId = eventForm.id || response.data?.id;
        const shareUrl = eventForm.publicationStatus === "published"
          ? (response.data?.registrationUrl || (savedId ? buildEventRegistrationUrl(savedId) : `${window.location.origin}/2.0/calendar`))
          : "";
        setLastCreatedShareUrl(shareUrl);
        setFormMessage(eventForm.publicationStatus === "published"
          ? "Calendar event saved and published. The registration/share link is ready below."
          : "Calendar event saved as a draft. Publish it when it is ready for visitors.");
        await fetchCalendarData();
      } else {
        if (!eventForm.eventDate || !eventForm.title.trim() || !eventForm.city.trim()) {
          setFormMessage("Please enter meetup title, city, and date.");
          setSavingEvent(false);
          return;
        }

        const payload = {
          title: eventForm.title,
          city: eventForm.city,
          state: eventForm.state,
          venueName: eventForm.venueName,
          addressLine: eventForm.addressLine,
          zipCode: eventForm.zipCode,
          eventDate: eventForm.eventDate,
          startTime: eventForm.startTime,
          endTime: eventForm.endTime,
          timezone: eventForm.timezone,
          topic: eventForm.topic,
          description: eventForm.description,
          walkInAllowed: Boolean(eventForm.walkInAllowed),
          onlineAvailable: Boolean(eventForm.onlineAvailable),
          capacity: eventForm.meetupCapacity === "" ? null : Number(eventForm.meetupCapacity),
          status: eventForm.meetupStatus,
          publicationStatus: eventForm.meetupPublicationStatus,
        };

        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/community-meetups`,
          payload,
          { headers: authHeaders }
        );
        const shareUrl = eventForm.meetupPublicationStatus === "published"
          ? (response.data?.registrationUrl || buildMeetupShareUrl(response.data?.slug || response.data?.id || eventForm.title))
          : "";
        setLastCreatedShareUrl(shareUrl);
        setFormMessage(eventForm.meetupPublicationStatus === "published"
          ? "Community meetup saved and published. The registration/share link is ready below."
          : "Community meetup saved as a draft. Publish it when it is ready for visitors.");
        await fetchCalendarData();
        setMeetupRefreshKey((current) => current + 1);
      }
    } catch (error) {
      setFormMessage(error?.response?.data?.message || "Unable to save calendar item.");
    } finally {
      setSavingEvent(false);
    }
  };

  const copyShareLink = async (shareUrl) => {
    if (!shareUrl) return;
    await copyText(shareUrl);
  };

  const openSocialShare = (shareUrl, text, channel) => {
    if (!shareUrl) return;
    window.open(buildSocialUrl(shareUrl, text, channel), "_blank", "noopener,noreferrer");
  };

  return (
    <div className={`calendar-page ${isAdmin ? "calendar-admin-mode" : ""}`}>
      <div className="calendar-page-header">
        <div>
          <span className="calendar-pill">Udyog Connect Calendar</span>
          <h1>Events, Meetups & Training Programs</h1>
          <p>
            Search upcoming Udyog Connect events, community meetups, workshops,
            training sessions, and partnership programs in one place.
          </p>
        </div>
        <div className="calendar-summary-card">
          <strong>{calendarEvents.length}</strong>
          <span>Matching items</span>
        </div>
      </div>

      {isAdmin && (
        <div className="calendar-admin-panel">
          <div>
            <strong>Admin calendar editing is on</strong>
            <span>Click any day in the calendar to add an event, or create a UC Community Meetup with share links.</span>
          </div>
          <div className="calendar-admin-actions">
            <button type="button" onClick={() => openCreateCalendarItem(toInputDate(new Date()), "event")}>
              Add calendar event
            </button>
            <button type="button" className="secondary" onClick={() => openCreateCalendarItem(toInputDate(new Date()), "meetup")}>
              Add community meetup
            </button>
          </div>
        </div>
      )}

      <CommunityMeetups variant="calendar" refreshKey={meetupRefreshKey} />

      <div className="calendar-filter-panel">
        <div className="calendar-search">
          <label htmlFor="calendar-search">Search programs</label>
          <input
            id="calendar-search"
            type="search"
            placeholder="Search by title, partner, audience, location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="calendar-filter-grid">
          <label>
            Type
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">All types</option>
              <option value="Udyog Connect Event">Udyog Connect Events</option>
              <option value="UC Community Meetup">UC Community Meetups</option>
              <option value="Training Program">Training Programs</option>
            </select>
          </label>

          <label>
            Partner
            <select value={partnerFilter} onChange={(e) => setPartnerFilter(e.target.value)}>
              <option value="all">All partners</option>
              {partners.map((partner) => (
                <option key={partner} value={partner}>
                  {partner}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className="calendar-reset-btn"
            onClick={() => {
              setSearchTerm("");
              setTypeFilter("all");
              setPartnerFilter("all");
            }}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="calendar-legend">
        <span><i className="legend-dot event-dot" /> Udyog Connect Events</span>
        <span><i className="legend-dot meetup-dot" /> UC Community Meetups</span>
        <span><i className="legend-dot training-dot" /> Training Programs</span>
      </div>

      <div className="calendar-content-grid">
        <div className="calendar-wrapper">
          {loading ? (
            <div className="calendar-loading">Loading calendar...</div>
          ) : (
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin, listPlugin]}
              initialView={isMobile ? "listMonth" : "dayGridMonth"}
              headerToolbar={
                isMobile
                  ? {
                      left: "prev,next today",
                      center: "title",
                      right: "",
                    }
                  : {
                      left: "prev,next today",
                      center: "title",
                      right: "dayGridMonth,listMonth",
                    }
              }
              views={{
                dayGridMonth: {
                  titleFormat: { year: "numeric", month: "long" },
                },
                listMonth: {
                  titleFormat: { year: "numeric", month: "long" },
                },
              }}
              buttonText={{
                today: "Today",
                dayGridMonth: "Month",
                listMonth: "List",
              }}
              events={calendarEvents}
              eventClick={handleEventClick}
              dateClick={(info) => openCreateCalendarItem(info.dateStr, "event")}
              selectable={isAdmin}
              height="auto"
              fixedWeekCount={false}
              dayMaxEvents={isMobile ? 1 : 3}
              displayEventTime={true}
              noEventsContent="No events, meetups, or training programs to display"
            />
          )}
        </div>

        <aside className="calendar-side-panel">
          <h2>Calendar items</h2>
          <p className="calendar-side-panel-note">Newest first — including past events and meetups.</p>
          <div className="calendar-card-list">
            {calendarArchiveEvents.length > 0 ? (
              calendarArchiveEvents.map((event) => (
                <button
                  type="button"
                  key={event.id}
                  className="calendar-result-card"
                  onClick={() => openEventDetails(event)}
                >
                  <span className={`result-type ${
                    event.extendedProps.type === "Training Program"
                      ? "training"
                      : event.extendedProps.type === "UC Community Meetup"
                        ? "meetup"
                        : "event"
                  }`}>
                    {event.extendedProps.type}
                  </span>
                  <strong>{event.title}</strong>
                  <small>{formatDateTime(new Date(event.start))}</small>
                  <span>{event.extendedProps.partner || "Udyog Connect"} | {event.extendedProps.location || "TBA"}</span>
                </button>
              ))
            ) : (
              <div className="calendar-empty-card">No matching programs found.</div>
            )}
          </div>
        </aside>
      </div>

      {eventFormOpen && (
        <div className="event-modal-overlay" onClick={() => setEventFormOpen(false)}>
          <div className="event-modal calendar-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="event-modal-header">
              <div>
                <span className="event-type-chip">Admin editor</span>
                <h2>{eventFormMode === "edit" ? "Edit calendar event" : "Create calendar item"}</h2>
              </div>
              <button className="event-close-btn" onClick={() => setEventFormOpen(false)}>
                Close
              </button>
            </div>

            <form className="calendar-edit-form" onSubmit={submitCalendarForm}>
              <label>
                Item type
                <select
                  value={eventForm.kind}
                  disabled={eventFormMode === "edit"}
                  onChange={(e) => updateEventForm("kind", e.target.value)}
                >
                  <option value="event">Calendar Event</option>
                  <option value="meetup">UC Community Meetup</option>
                </select>
              </label>

              {eventForm.kind === "event" ? (
                <>
                  <label className="wide">
                    Event name
                    <input
                      value={eventForm.title}
                      onChange={(e) => updateEventForm("title", e.target.value)}
                      placeholder="Example: Johns Creek UC meet up"
                      required
                    />
                  </label>
                  <label className="wide">
                    Event category
                    <input
                      value={eventForm.eventCategory}
                      onChange={(e) => updateEventForm("eventCategory", e.target.value)}
                      placeholder="Example: Udyog Connect Event"
                    />
                  </label>
                  <label className="wide">
                    Rich event content
                    <textarea
                      value={eventForm.description}
                      onChange={(e) => updateEventForm("description", e.target.value)}
                      placeholder="Example: <h2>About this event</h2><p>Add formatted details here.</p>"
                      rows={7}
                      required
                    />
                  </label>
                  <div className="calendar-edit-grid">
                    <label>
                      Date
                      <input type="date" value={eventForm.eventDate} onChange={(e) => updateEventForm("eventDate", e.target.value)} required />
                    </label>
                    <label>
                      Start
                      <input type="time" value={eventForm.startTime} onChange={(e) => updateEventForm("startTime", e.target.value)} required />
                    </label>
                    <label>
                      End
                      <input type="time" value={eventForm.endTime} onChange={(e) => updateEventForm("endTime", e.target.value)} required />
                    </label>
                  </div>
                  <label className="wide">
                    Venue
                    <input value={eventForm.venue} onChange={(e) => updateEventForm("venue", e.target.value)} required />
                  </label>
                  <div className="calendar-edit-grid">
                    <label>
                      Seat capacity
                      <input type="number" min="0" value={eventForm.capacity} onChange={(e) => updateEventForm("capacity", e.target.value)} placeholder="Blank = unlimited" />
                    </label>
                    <label>
                      Status
                      <select value={eventForm.status} onChange={(e) => updateEventForm("status", e.target.value)}>
                        <option value="open">Open</option>
                        <option value="closed">Closed</option>
                      </select>
                    </label>
                    <label>
                      Publication
                      <select value={eventForm.publicationStatus} onChange={(e) => updateEventForm("publicationStatus", e.target.value)}>
                        <option value="draft">Draft — admin only</option>
                        <option value="published">Published — public</option>
                      </select>
                    </label>
                    <label>
                      Who can apply
                      <select value={eventForm.whoCanApply} onChange={(e) => updateEventForm("whoCanApply", e.target.value)}>
                        <option value="anyone">Anyone</option>
                        <option value="member">Members only</option>
                      </select>
                    </label>
                    <label>
                      Native registration
                      <select value={String(eventForm.nativeRegistrationEnabled)} onChange={(e) => updateEventForm("nativeRegistrationEnabled", e.target.value === "true")}>
                        <option value="true">Enabled</option>
                        <option value="false">Disabled</option>
                      </select>
                    </label>
                    <label>
                      Main interest field
                      <select value={String(eventForm.showPrimaryInterest)} onChange={(e) => updateEventForm("showPrimaryInterest", e.target.value === "true")}>
                        <option value="true">Show on registration</option>
                        <option value="false">Hide on registration</option>
                      </select>
                    </label>
                  </div>
                  <label className="wide">
                    External registration link (optional)
                    <input value={eventForm.link} onChange={(e) => updateEventForm("link", e.target.value)} placeholder="Optional external registration link" />
                  </label>
                  <div className="calendar-edit-grid">
                    <label>
                      Payment
                      <select value={String(eventForm.paymentEnabled)} onChange={(e) => updateEventForm("paymentEnabled", e.target.value === "true")}>
                        <option value="false">Free event</option>
                        <option value="true">Paid / payment follow-up</option>
                      </select>
                    </label>
                    <label>
                      Amount
                      <input type="number" min="0" step="0.01" value={eventForm.paymentAmountUsd} onChange={(e) => updateEventForm("paymentAmountUsd", e.target.value)} disabled={!eventForm.paymentEnabled} />
                    </label>
                    <label>
                      Payment label
                      <input value={eventForm.paymentLabel} onChange={(e) => updateEventForm("paymentLabel", e.target.value)} disabled={!eventForm.paymentEnabled} />
                    </label>
                  </div>
                </>
              ) : (
                <>
                  <label className="wide">
                    Meetup title
                    <input value={eventForm.title} onChange={(e) => updateEventForm("title", e.target.value)} required />
                  </label>
                  <div className="calendar-edit-grid">
                    <label>
                      Date
                      <input type="date" value={eventForm.eventDate} onChange={(e) => updateEventForm("eventDate", e.target.value)} required />
                    </label>
                    <label>
                      Start
                      <input type="time" value={eventForm.startTime} onChange={(e) => updateEventForm("startTime", e.target.value)} />
                    </label>
                    <label>
                      End
                      <input type="time" value={eventForm.endTime} onChange={(e) => updateEventForm("endTime", e.target.value)} />
                    </label>
                  </div>
                  <div className="calendar-edit-grid">
                    <label>
                      City
                      <input value={eventForm.city} onChange={(e) => updateEventForm("city", e.target.value)} required />
                    </label>
                    <label>
                      State
                      <input value={eventForm.state} onChange={(e) => updateEventForm("state", e.target.value)} />
                    </label>
                    <label>
                      Capacity
                      <input type="number" min="0" value={eventForm.meetupCapacity} onChange={(e) => updateEventForm("meetupCapacity", e.target.value)} />
                    </label>
                  </div>
                  <label className="wide">
                    Venue / meeting room
                    <input value={eventForm.venueName} onChange={(e) => updateEventForm("venueName", e.target.value)} />
                  </label>
                  <label className="wide">
                    Address / meeting note
                    <input value={eventForm.addressLine} onChange={(e) => updateEventForm("addressLine", e.target.value)} />
                  </label>
                  <label className="wide">
                    Topic
                    <input value={eventForm.topic} onChange={(e) => updateEventForm("topic", e.target.value)} />
                  </label>
                  <label className="wide">
                    Description
                    <textarea value={eventForm.description} onChange={(e) => updateEventForm("description", e.target.value)} rows={3} />
                  </label>
                  <div className="calendar-edit-grid">
                    <label>
                      Walk-ins
                      <select value={String(eventForm.walkInAllowed)} onChange={(e) => updateEventForm("walkInAllowed", e.target.value === "true")}>
                        <option value="true">Allowed</option>
                        <option value="false">Registration only</option>
                      </select>
                    </label>
                    <label>
                      Online option
                      <select value={String(eventForm.onlineAvailable)} onChange={(e) => updateEventForm("onlineAvailable", e.target.value === "true")}>
                        <option value="true">Available</option>
                        <option value="false">Not available</option>
                      </select>
                    </label>
                    <label>
                      Status
                      <select value={eventForm.meetupStatus} onChange={(e) => updateEventForm("meetupStatus", e.target.value)}>
                        <option value="scheduled">Scheduled</option>
                        <option value="full">Full</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="completed">Completed</option>
                      </select>
                    </label>
                    <label>
                      Publication
                      <select value={eventForm.meetupPublicationStatus} onChange={(e) => updateEventForm("meetupPublicationStatus", e.target.value)}>
                        <option value="draft">Draft — admin only</option>
                        <option value="published">Published — public</option>
                      </select>
                    </label>
                  </div>
                </>
              )}

              {formMessage && <div className="calendar-form-message">{formMessage}</div>}

              {lastCreatedShareUrl && (
                <div className="calendar-share-panel calendar-created-share-panel">
                  <strong>Registration / share link</strong>
                  <span>{lastCreatedShareUrl}</span>
                  <div>
                    <button type="button" onClick={() => copyShareLink(lastCreatedShareUrl)}>
                      Copy link
                    </button>
                    <button type="button" onClick={() => window.open(lastCreatedShareUrl, "_blank", "noopener,noreferrer")}>
                      Open link
                    </button>
                  </div>
                </div>
              )}

              <div className="calendar-edit-actions">
                <button type="submit" disabled={savingEvent}>
                  {savingEvent ? "Saving…" : "Save"}
                </button>
                <button type="button" className="secondary" onClick={() => setEventFormOpen(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedEvent && (
        <div
          className="event-modal-overlay"
          onClick={() => setSelectedEvent(null)}
        >
          <div
            className="event-modal calendar-detail-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="event-modal-header">
              <div>
                <span className="event-type-chip">{selectedEvent.type}</span>
                <h2>{selectedEvent.title}</h2>
              </div>
              <button
                className="event-close-btn"
                onClick={() => setSelectedEvent(null)}
              >
                Close
              </button>
            </div>

            <div className="event-primary-actions">
              {selectedEvent.applyLink ? (
                <a
                  className="event-apply-link event-apply-link-primary"
                  href={selectedEvent.applyLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Register / RSVP
                </a>
              ) : (
                <span className="event-registration-note">
                  Registration link is not available yet.
                </span>
              )}
              {selectedEvent.shareUrl && (
                <button type="button" onClick={() => copyShareLink(selectedEvent.shareUrl)}>
                  Copy share link
                </button>
              )}
              {selectedEvent.capacity && (
                <span className="event-seat-note">
                  {Math.max(0, Number(selectedEvent.seatsLeft ?? selectedEvent.capacity))} of {selectedEvent.capacity} seats left
                </span>
              )}
              {selectedEvent.paymentSettings?.enabled && (
                <span className="event-payment-note">
                  ${Number(selectedEvent.paymentSettings.amountUsd || 0).toFixed(2)} · {selectedEvent.paymentSettings.label || "Registration"}
                </span>
              )}
            </div>

            {selectedEvent.images?.length > 0 && (
              <div className="event-image-gallery event-image-gallery-top">
                {selectedEvent.images.map((image) => (
                  <img key={image.id} src={image.url} alt={selectedEvent.title} />
                ))}
              </div>
            )}

            <div className="event-detail-row">
              <span className="event-label">Start</span>
              <span>{formatDateTime(selectedEvent.start)}</span>
            </div>

            {selectedEvent.end && (
              <div className="event-detail-row">
                <span className="event-label">End</span>
                <span>{formatDateTime(selectedEvent.end)}</span>
              </div>
            )}

            <div className="event-detail-row">
              <span className="event-label">Location</span>
              <span>{selectedEvent.location || "TBA"}</span>
            </div>

            <div className="event-detail-row">
              <span className="event-label">Partner</span>
              <span>{selectedEvent.partner || "Udyog Connect"}</span>
            </div>

            <div className="event-detail-row">
              <span className="event-label">Audience</span>
              <span>{selectedEvent.audience || "Open to community"}</span>
            </div>

            {selectedEvent.status && (
              <div className="event-detail-row">
                <span className="event-label">Status</span>
                <span>{selectedEvent.status}</span>
              </div>
            )}

            <div className="event-description-block">
              <div className="event-label description-label">Description</div>
              <div
                className="event-rich-description"
                dangerouslySetInnerHTML={buildRichHtmlMarkup(selectedEvent.description)}
              />
            </div>

            {selectedEvent.shareUrl && (
              <div className="calendar-share-panel">
                <strong>Share this event</strong>
                <span>{selectedEvent.shareUrl}</span>
                <div>
                  <button type="button" onClick={() => copyShareLink(selectedEvent.shareUrl)}>
                    Copy link
                  </button>
                  <button type="button" onClick={() => openSocialShare(selectedEvent.shareUrl, selectedEvent.title, "whatsapp")}>
                    WhatsApp
                  </button>
                  <button type="button" onClick={() => openSocialShare(selectedEvent.shareUrl, selectedEvent.title, "facebook")}>
                    Facebook
                  </button>
                  <button type="button" onClick={() => openSocialShare(selectedEvent.shareUrl, selectedEvent.title, "linkedin")}>
                    LinkedIn
                  </button>
                </div>
              </div>
            )}

            <div className="event-modal-actions">
              {selectedEvent.applyLink && (
                <a
                  className="event-apply-link"
                  href={selectedEvent.applyLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  Apply / Register
                </a>
              )}
              {isAdmin && selectedEvent.sourceType === "event" && (
                <button type="button" className="event-admin-edit-btn" onClick={() => openEditCalendarEvent(selectedEvent)}>
                  Edit event
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
